<script setup>
import Header from "@/components/Header.vue";
import FighterCard from "@/components/FighterCard.vue";
import heroImage from "@/assets/img/background/image3.png";
import { loadSlim } from "tsparticles-slim";
import { getPeleadoresConfirmados } from "@/utils/api_evento";
import { ref, onMounted, computed } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay, Pagination, EffectCoverflow } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";

// ─── Refs por división ────────────────────────────────────────────────────────
const divMasc = ref([]);
const divFem = ref([]);
const divTeens = ref([]);
const divKids = ref([]);
const cargando = ref(true);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
  return edad;
};

const mapearPeleador = (p) => ({
  nombre: `${p.nombre} ${p.apellido}`.trim(),
  imagen: p.foto,
  bandera: p.nacionalidad
    ? `https://flagcdn.com/w80/${p.nacionalidad.toLowerCase()}.png`
    : null,
  edad: calcularEdad(p.fecha_nacimiento),
  cinta: p.cinta?.toLowerCase() ?? "",
  equipo: p.equipo,
  instagram: p.redes_sociales?.instagram ?? null,
  facebook: p.redes_sociales?.facebook ?? null,
  youtube: p.redes_sociales?.youtube ?? null,
  twitter: p.redes_sociales?.twitter ?? null,
  // ✅ Peleador marcado por Armando para aparecer al centro por default.
  destacado: !!p.destacado_carousel,
});

// Mueve al peleador marcado como `destacado` al inicio de la lista, para que
// con centeredSlides sea el que se muestre al centro al cargar el carrusel.
// Si nadie está marcado, se deja el orden tal cual viene del back.
const ordenarConDestacadoAlCentro = (lista) => {
  const idx = lista.findIndex((p) => p.destacado);
  if (idx <= 0) return lista;
  const copia = [...lista];
  const [destacado] = copia.splice(idx, 1);
  copia.unshift(destacado);
  return copia;
};

// ─── Clasificar por edad y género ────────────────────────────────────────────
// Kids  : < 13 años
// Teens : 13 – 17 años
// Adultos: 18+ → Masculino (H) o Femenino (M)
const clasificar = (peleadores) => {
  const masc = [];
  const fem = [];
  const teens = [];
  const kids = [];

  for (const p of peleadores) {
    const edad = calcularEdad(p.fecha_nacimiento);

    if (edad !== null && edad < 13) {
      kids.push(mapearPeleador(p));
    } else if (edad !== null && edad < 18) {
      teens.push(mapearPeleador(p));
    } else if (p.genero === "H") {
      masc.push(mapearPeleador(p));
    } else {
      fem.push(mapearPeleador(p));
    }
  }

  return {
    masc: ordenarConDestacadoAlCentro(masc),
    fem: ordenarConDestacadoAlCentro(fem),
    teens: ordenarConDestacadoAlCentro(teens),
    kids: ordenarConDestacadoAlCentro(kids),
  };
};

onMounted(async () => {
  try {
    const todos = await getPeleadoresConfirmados();
    const result = clasificar(todos);
    divMasc.value = result.masc;
    divFem.value = result.fem;
    divTeens.value = result.teens;
    divKids.value = result.kids;
  } catch (error) {
    console.error("Error cargando peleadores:", error);
  } finally {
    cargando.value = false;
  }
});

// ─── Computed helpers por división ────────────────────────────────────────────
const hayDatos = computed(
  () =>
    divMasc.value.length > 0 ||
    divFem.value.length > 0 ||
    divTeens.value.length > 0 ||
    divKids.value.length > 0
);

// Devuelve true si ≤ 4 → grid; false → swiper
const modoGrid = (lista) => lista.length <= 4;
// ✅ Sin loop en ninguna división: con coverflow + slidesPerView:"auto" el
// loop nunca se sintió tan fluido como el deslizamiento simple de lado a
// lado, ni siquiera en la masculina (22+). centeredSlidesBounds:true evita
// que al llegar al final quede "colgado" sin nada a la derecha para
// centrar (el bug original que en su momento motivó probar el loop).
const usarLoop = () => false;

// ─── Configuración Swiper ─────────────────────────────────────────────────────
const swiperConfig = {
  modules: [Autoplay, Pagination, EffectCoverflow],
  effect: "coverflow",
  grabCursor: true,
  centeredSlides: true,
  centeredSlidesBounds: true,
  // Sin loop, el autoplay llegaría al final y se quedaría ahí. rewind hace
  // que, al terminar, regrese directo a la primera tarjeta y siga el ciclo
  // — sin el salto constante del loop en cada slide, solo un reset al final.
  rewind: true,
  slidesPerView: "auto",
  coverflowEffect: {
    rotate: 10,
    stretch: 30,
    depth: 100,
    modifier: 1.4,
    slideShadows: false,
  },
  pagination: { clickable: true },
};

// ─── Utilidades visuales ──────────────────────────────────────────────────────
const obtenerColorCinta = (cinta) =>
  ({
    blanca: "#e8e8e8",
    azul: "#0044ee",
    morada: "#7a00cc",
    cafe: "#5c3010",
    negra: "#111111",
    verde: "#007a00",
    amarilla: "#e6b800",
    amarilla_naranja: "#e87500",
    naranja: "#e85c00",
    naranja_verde: "#4a8c00",
    blanca_amarilla: "#c8b800",
  }[cinta] || "#888");

const obtenerNombreCinta = (cinta) =>
  ({
    blanca: "Blanca",
    azul: "Azul",
    morada: "Morada",
    cafe: "Café",
    negra: "Negra",
    verde: "Verde",
    amarilla: "Amarilla",
    amarilla_naranja: "Amarilla-Naranja",
    naranja: "Naranja",
    naranja_verde: "Naranja-Verde",
    blanca_amarilla: "Blanca-Amarilla",
  }[cinta] ?? cinta.charAt(0).toUpperCase() + cinta.slice(1));

// ─── Particles ────────────────────────────────────────────────────────────────
const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#00aaff" },
    shape: { type: "circle" },
    opacity: { value: 0.25 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};
const particlesInit = async (engine) => await loadSlim(engine);

// ─── Definición de divisiones (orden y estilo) ────────────────────────────────
const divisiones = computed(() => [
  {
    key: "masc",
    label: "División Masculina",
    clase: "masculino",
    lista: divMasc.value,
    delay: 0,
    autoplayDelay: 3200,
  },
  {
    key: "fem",
    label: "División Femenina",
    clase: "femenino",
    lista: divFem.value,
    delay: 100,
    autoplayDelay: 3600,
  },
  {
    key: "teens",
    label: "División Teens",
    clase: "teens",
    lista: divTeens.value,
    delay: 200,
    autoplayDelay: 3400,
  },
  {
    key: "kids",
    label: "División Kids",
    clase: "kids",
    lista: divKids.value,
    delay: 300,
    autoplayDelay: 3800,
  },
]);
</script>

<template>
  <section id="peleadores" class="peleadores-wrapper">
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="peleadoresParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>

    <div class="contenido">
      <Header />

      <div class="titulo-row" data-aos="fade-up">
        <span class="titulo-linea"></span>
        <h2 class="titulo-seccion">Peleadores <em>Estelares</em></h2>
        <span class="titulo-linea"></span>
      </div>

      <!-- ══ CARGANDO ══ -->
      <div v-if="cargando" class="estado-centro">
        <div class="spinner">
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>

      <!-- ══ DIVISIONES ══ -->
      <template v-else-if="hayDatos">
        <template v-for="div in divisiones" :key="div.key">
          <div
            v-if="div.lista.length > 0"
            class="seccion-genero"
            data-aos="fade-up"
            :data-aos-delay="div.delay"
          >
            <!-- Encabezado de división -->
            <div class="genero-header">
              <span class="genero-chip" :class="div.clase">
                {{ div.label.toUpperCase() }}
              </span>
            </div>

            <!-- Carrusel (> 4 peleadores) -->
            <div v-if="!modoGrid(div.lista)" class="carousel-wrap">
              <swiper
                v-bind="swiperConfig"
                :loop="usarLoop(div.lista)"
                :autoplay="{
                  delay: div.autoplayDelay,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }"
                class="fighters-swiper"
              >
                <swiper-slide v-for="(p, i) in div.lista" :key="i" class="fighter-slide">
                  <FighterCard
                    :peleador="p"
                    :obtenerColorCinta="obtenerColorCinta"
                    :obtenerNombreCinta="obtenerNombreCinta"
                  />
                </swiper-slide>
              </swiper>
            </div>

            <!-- Grid (≤ 4 peleadores) -->
            <div v-else class="grid-wrap" :class="`n${div.lista.length}`">
              <div v-for="(p, i) in div.lista" :key="i" class="grid-card">
                <FighterCard
                  :peleador="p"
                  :obtenerColorCinta="obtenerColorCinta"
                  :obtenerNombreCinta="obtenerNombreCinta"
                />
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- ══ VACÍO ══ -->
      <div v-else class="estado-centro">
        <div class="vacio-card">
          <i class="bi bi-shield-star vacio-icon"></i>
          <p class="vacio-msg">¡Los combates estelares se revelarán pronto!</p>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.peleadores-wrapper {
  position: relative;
  min-height: 100vh;
  background: #000;
  overflow: hidden;
}
.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.22) saturate(0.5);
  z-index: 1;
}
.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    #000 0%,
    transparent 25%,
    transparent 75%,
    #000 100%
  );
  z-index: 2;
}
#peleadoresParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}
.smoke-layer {
  position: absolute;
  inset: 0;
  background: src("/assets/backgrounds/image3.png");
  opacity: 0.22;
  mix-blend-mode: lighten;
  animation: smokeMove 20s infinite linear;
  z-index: 4;
}
@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.2);
  }
  100% {
    transform: translateY(-20%) scale(1.25);
  }
}
.contenido {
  position: relative;
  z-index: 10;
}
.titulo-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding-top: 4rem;
  padding-bottom: 0.5rem;
}
.titulo-linea {
  flex: 1;
  max-width: 120px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(204, 0, 0, 0.55),
    rgba(220, 60, 60, 0.5),
    rgba(204, 0, 0, 0.55),
    transparent
  );
}

.titulo-seccion {
  font-family: "Oswald", "Impact", sans-serif;
  font-size: clamp(1.4rem, 4vw, 2rem);
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 2px;
  white-space: nowrap;
  margin: 0;
}
.titulo-seccion em {
  font-style: normal;
  color: transparent;
  -webkit-text-stroke: 1.5px #cc0000;
}
.seccion-genero {
  padding-bottom: 2rem;
}
.genero-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 0 0.75rem 48px;
}
.estado-centro {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
}

/* ── Spinner ───────────────────────────────────── */
.spinner {
  position: relative;
  width: 44px;
  height: 44px;
}
.spinner div {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #00aaff;
  animation: giro 1s linear infinite;
}
.spinner div:nth-child(2) {
  inset: 7px;
  border-top-color: rgba(0, 170, 255, 0.5);
  animation-duration: 1.6s;
  animation-direction: reverse;
}
.spinner div:nth-child(3) {
  inset: 14px;
  border-top-color: rgba(0, 170, 255, 0.2);
  animation-duration: 2.2s;
}
@keyframes giro {
  to {
    transform: rotate(360deg);
  }
}

/* ── Swiper / Grid ─────────────────────────────── */
.carousel-wrap {
  padding: 0 0 20px;
}
.fighters-swiper {
  padding-bottom: 44px !important;
}
.fighter-slide {
  width: 300px;
  height: 440px;
}
.grid-wrap {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
  padding: 0 32px 40px;
}
.grid-card {
  width: 300px;
  height: 440px;
}
.grid-wrap.n1 .grid-card {
  width: min(400px, 90vw);
  height: 560px;
}
.grid-wrap.n2 .grid-card {
  width: min(360px, 44vw);
  height: 520px;
}
.grid-wrap.n3 .grid-card {
  width: min(310px, 30vw);
  height: 480px;
}
.grid-wrap.n4 .grid-card {
  width: min(280px, 22vw);
  height: 440px;
}

/* ── Swiper bullets ────────────────────────────── */
:deep(.swiper-pagination-bullet) {
  background: rgba(255, 255, 255, 0.2);
  width: 7px;
  height: 7px;
  opacity: 1;
  transition: all 0.3s;
}
:deep(.swiper-pagination-bullet-active) {
  background: #00aaff;
  width: 22px;
  border-radius: 4px;
  box-shadow: 0 0 7px rgba(0, 170, 255, 0.6);
}

/* ── Chips de división ─────────────────────────── */
.genero-chip {
  font-family: "Oswald", sans-serif;
  font-weight: bold;
  font-size: 1rem;
  letter-spacing: 2px;
  padding: 6px 16px;
  position: relative;
  text-transform: uppercase;
}
.genero-chip::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 4px;
  opacity: 0.15;
}

/* Masculino — azul */
.genero-chip.masculino {
  color: #00aaff;
}
.genero-chip.masculino::before {
  background: linear-gradient(90deg, #00aaff, transparent);
}

/* Femenino — rosa */
.genero-chip.femenino {
  color: #ff4da6;
}
.genero-chip.femenino::before {
  background: linear-gradient(90deg, #ff4da6, transparent);
}

/* Teens — verde-lima */
.genero-chip.teens {
  color: #7fff00;
}
.genero-chip.teens::before {
  background: linear-gradient(90deg, #7fff00, transparent);
}

/* Kids — naranja-dorado */
.genero-chip.kids {
  color: #ffaa00;
}
.genero-chip.kids::before {
  background: linear-gradient(90deg, #ffaa00, transparent);
}

/* ── Vacío ─────────────────────────────────────── */
.vacio-card {
  text-align: center;
  padding: 40px 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 170, 255, 0.12);
  border-radius: 20px;
  max-width: 400px;
}
.vacio-icon {
  font-size: 3rem;
  color: #00aaff;
  display: block;
  margin-bottom: 16px;
  filter: drop-shadow(0 0 8px rgba(0, 170, 255, 0.4));
}
.vacio-msg {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  margin: 0;
}

/* ── Responsive ────────────────────────────────── */
@media (max-width: 768px) {
  .fighter-slide,
  .grid-card {
    width: 270px;
    height: 400px;
  }
  .grid-wrap.n2 .grid-card,
  .grid-wrap.n3 .grid-card,
  .grid-wrap.n4 .grid-card {
    width: min(300px, 85vw);
    height: 440px;
  }
  .genero-header {
    padding-left: 24px;
  }
}
@media (max-width: 480px) {
  .titulo-seccion {
    font-size: 1.2rem;
  }
  .fighter-slide,
  .grid-card {
    width: 260px;
    height: 380px;
  }
}
</style>
